import React, { useCallback, useEffect, useState } from 'react';
import { authFetch, API } from './api';

const R = `${API}/returns`;

/**
 * Per-store refund rules.
 *
 * Replaces the old per-platform fee panel, which edited return_calc_settings —
 * a table nothing reads any more. Everything here is driven by the backend
 * registry: rule types and their parameters come from /refund-rules/catalogue,
 * so adding a rule class in refund_rules.py makes it appear here with no
 * frontend change. The preview is computed by the SAME backend code that prices
 * real refunds, rather than a JavaScript reimplementation that can drift.
 */

type ParamSpec = {
  name: string; type: string; label: string;
  default?: any; help?: string;
};
type RuleRow = {
  rule_key: string; label: string; help: string;
  enabled: boolean; sort_order: number;
  params: Record<string, any>;
  param_spec: ParamSpec[];
  updated_by?: string | null; updated_at?: string | null;
};

// The four scenarios the panel prices as you edit. Chosen to straddle the
// thresholds so a mis-set number is visible rather than plausible.
const SCENARIOS = [
  { label: 'Small return, free shipping', items: 100, returned: 15, paid: 0 },
  { label: 'Small return, paid shipping', items: 100, returned: 15, paid: 9.99 },
  { label: 'Large return, free shipping', items: 100, returned: 50, paid: 0 },
  { label: 'Whole order back, free shipping', items: 100, returned: 100, paid: 0 },
];

export default function RefundRules() {
  const isAdmin = (localStorage.getItem('wms_role') || 'staff') === 'admin';
  const [stores, setStores] = useState<any[]>([]);
  const [store, setStore] = useState<string>('');
  const [rules, setRules] = useState<RuleRow[]>([]);
  // the currencies this store's orders are in — from the backend, not assumed
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [previews, setPreviews] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    authFetch(`${API}/settings/webshops`).then(r => r.json()).then((ws: any[]) => {
      const list = Array.isArray(ws) ? ws : [];
      setStores(list);
      if (list.length && !store) setStore(list[0].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback((ws: string) => {
    if (!ws) return;
    authFetch(`${R}/refund-rules?webshop_id=${encodeURIComponent(ws)}`)
      .then(r => r.json()).then(d => { setRules(d.rules || []); setCurrencies(d.currencies || []); })
      .catch(() => setErr('Could not load the rules'));
  }, []);
  useEffect(() => { load(store); }, [store, load]);

  // Money labels were a hardcoded "€". Big Oliver and Nora sell in EUR only, but
  // Motley Denim's orders are in eight currencies and a threshold there is read in
  // the ORDER's currency (69 = 69 SEK on a Swedish order) — so the label says so,
  // instead of printing € over a number that is not euros. (F6, 2026-09-09)
  const SYMBOL: Record<string, string> = { EUR: '€', SEK: 'kr', NOK: 'kr', DKK: 'kr', CZK: 'Kč', HUF: 'Ft', RON: 'lei', PLN: 'zł' };
  const oneCurrency = currencies.length === 1 ? currencies[0] : null;
  const unit = oneCurrency ? (SYMBOL[oneCurrency] || oneCurrency) : 'order currency';
  const money = (n: number) => oneCurrency
    ? (oneCurrency === 'EUR' ? `€${n.toFixed(2)}` : `${n.toFixed(2)} ${oneCurrency}`)
    : n.toFixed(2);

  // Price the scenarios through the backend, using the rules as currently edited
  // (not as saved) so the table reflects what you are about to save.
  useEffect(() => {
    if (!store || !rules.length) { setPreviews([]); return; }
    const payload = rules.map(r => ({ rule_key: r.rule_key, params: r.params, enabled: r.enabled }))
                         .filter(r => r.enabled);
    Promise.all(SCENARIOS.map(sc =>
      authFetch(`${R}/refund-rules/preview`, {
        method: 'POST',
        body: JSON.stringify({
          webshop_id: store, rules: payload,
          returned_value: sc.returned, items_total: sc.items,
          shipping_paid: sc.paid, prior_returned: 0,
          order_country: 'EE', method_standard_rate: 5.99,
          method_free_eligible: sc.paid === 0,
        }),
      }).then(r => r.json()).then(d => ({ ...sc, ...d })))
    ).then(setPreviews).catch(() => {});
  }, [store, rules]);

  const setParam = (key: string, name: string, value: any) =>
    setRules(rs => rs.map(r => r.rule_key === key
      ? { ...r, params: { ...r.params, [name]: value } } : r));

  const toggle = (key: string) =>
    setRules(rs => rs.map(r => r.rule_key === key ? { ...r, enabled: !r.enabled } : r));

  const save = async () => {
    setSaving(true); setErr('');
    const res = await authFetch(`${R}/refund-rules`, {
      method: 'PUT',
      body: JSON.stringify({
        webshop_id: store,
        rules: rules.map(r => ({
          rule_key: r.rule_key, enabled: r.enabled,
          sort_order: r.sort_order, params: r.params,
        })),
      }),
    });
    setSaving(false);
    if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 1800); load(store); }
    else setErr('Could not save — admin only');
  };

  const field = (rule: RuleRow, spec: ParamSpec) => {
    const v = rule.params[spec.name];
    const common = 'bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm';
    if (spec.type === 'bool') {
      return (
        <label key={spec.name} className="flex items-start gap-2 py-1">
          <input type="checkbox" className="mt-1" checked={!!v} disabled={!isAdmin}
                 onChange={e => setParam(rule.rule_key, spec.name, e.target.checked)} />
          <span>
            <span className="text-sm text-gray-300">{spec.label}</span>
            {spec.help && <span className="block text-xs text-gray-600">{spec.help}</span>}
          </span>
        </label>
      );
    }
    if (spec.type === 'csv') {
      return (
        <div key={spec.name} className="py-1">
          <div className="text-sm text-gray-300">{spec.label}</div>
          {spec.help && <div className="text-xs text-gray-600 mb-1">{spec.help}</div>}
          <input className={`${common} w-full`} disabled={!isAdmin}
                 value={Array.isArray(v) ? v.join(', ') : (v || '')}
                 onChange={e => setParam(rule.rule_key, spec.name,
                   e.target.value.split(',').map(s => s.trim().toUpperCase()).filter(Boolean))} />
        </div>
      );
    }
    const isNum = spec.type === 'money' || spec.type === 'percent';
    return (
      <div key={spec.name} className="py-1">
        <div className="text-sm text-gray-300">{spec.label}</div>
        {spec.help && <div className="text-xs text-gray-600 mb-1">{spec.help}</div>}
        <div className="flex items-center gap-1">
          {spec.type === 'money' && (
            <span className="text-gray-500 text-sm whitespace-nowrap"
                  title={oneCurrency ? `This store sells in ${oneCurrency}`
                         : `This store's orders are in ${currencies.join(', ')} — the amount is read in each order's own currency`}>
              {unit}
            </span>
          )}
          <input className={`${common} ${isNum ? 'w-32' : 'w-full'}`} disabled={!isAdmin}
                 type={isNum ? 'number' : 'text'} step={spec.type === 'percent' ? '1' : '0.01'}
                 value={v ?? ''}
                 onChange={e => setParam(rule.rule_key, spec.name,
                   isNum ? Number(e.target.value) : e.target.value)} />
          {spec.type === 'percent' && <span className="text-gray-500 text-sm">%</span>}
        </div>
      </div>
    );
  };

  const anyEnabled = rules.some(r => r.enabled);

  return (
    <div className="bg-gray-900/60 border border-gray-800 rounded-lg p-4">
      <h3 className="text-white font-semibold">Refund rules</h3>
      <p className="text-xs text-gray-500 mb-3">
        How the suggested refund on the Manual refunds tab is calculated.
        Rules are per store — two shops on the same platform can have completely
        different terms.
      </p>

      {err && <div className="mb-3 px-3 py-2 rounded bg-red-900/40 text-red-300 text-sm">{err}</div>}

      <div className="flex items-center gap-3 mb-4">
        <select value={store} onChange={e => setStore(e.target.value)}
                className="bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-sm">
          {stores.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}
        </select>
        {!anyEnabled && (
          <span className="text-xs text-amber-400">
            No rules enabled — this store refunds the full returned value.
          </span>
        )}
      </div>

      <div className="space-y-3">
        {rules.map(r => (
          <div key={r.rule_key}
               className={`rounded-lg border p-3 ${r.enabled
                 ? 'bg-gray-800/40 border-gray-700' : 'bg-gray-900/40 border-gray-800'}`}>
            <label className="flex items-start gap-2">
              <input type="checkbox" className="mt-1" checked={r.enabled} disabled={!isAdmin}
                     onChange={() => toggle(r.rule_key)} />
              <span>
                <span className={`font-medium ${r.enabled ? 'text-white' : 'text-gray-500'}`}>
                  {r.label}
                </span>
                <span className="block text-xs text-gray-500 mt-0.5">{r.help}</span>
              </span>
            </label>
            {r.enabled && (
              <div className="mt-2 pl-6 grid grid-cols-1 md:grid-cols-2 gap-x-6">
                {r.param_spec.map(spec => field(r, spec))}
              </div>
            )}
          </div>
        ))}
      </div>

      {isAdmin && (
        <div className="mt-4 flex items-center gap-3">
          <button onClick={save} disabled={saving}
                  className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-700 text-white
                             rounded px-4 py-2 text-sm font-medium">
            {saving ? 'Saving…' : 'Save rules'}
          </button>
          {saved && <span className="text-green-400 text-sm">Saved</span>}
        </div>
      )}

      {/* Priced by the backend — the same code that prices real refunds. */}
      <div className="mt-5 pt-4 border-t border-gray-800">
        <div className="text-xs uppercase tracking-wider text-gray-500 mb-2">
          Check your rules — priced live by the backend (free method {money(5.99)}, delivery to EE)
        </div>
        {!oneCurrency && currencies.length > 1 && (
          <div className="text-xs text-amber-400 mb-2">
            This store's orders are in {currencies.join(', ')}. Every amount in these rules is read in the
            order's own currency — a threshold of 69 means 69 SEK on a Swedish order and 69 NOK on a Norwegian one.
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-gray-500 text-xs">
              <tr>
                <th className="text-left py-1">Scenario</th>
                <th className="text-right">Items</th>
                <th className="text-right">Returned</th>
                <th className="text-right">Kept</th>
                <th className="text-right">Deducted</th>
                <th className="text-right">Suggested refund</th>
              </tr>
            </thead>
            <tbody>
              {previews.map((p, i) => {
                const deducted = Math.round((p.returned - (p.result?.net_refund ?? 0)) * 100) / 100;
                return (
                  <tr key={i} className="border-t border-gray-800/60">
                    <td className="py-1.5 text-gray-300">{p.label}</td>
                    <td className="text-right text-gray-500">{money(p.items)}</td>
                    <td className="text-right text-gray-400">{money(p.returned)}</td>
                    <td className="text-right text-gray-500">
                      {money(p.context?.kept_value ?? 0)}
                    </td>
                    <td className={`text-right ${deducted > 0 ? 'text-red-400' : 'text-gray-600'}`}>
                      {deducted > 0 ? `−${money(deducted)}` : '—'}
                    </td>
                    <td className="text-right text-green-400 font-medium">
                      {money(p.result?.net_refund ?? 0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {previews.some(p => (p.result?.flags || []).length > 0) && (
          <div className="mt-2 text-xs text-amber-400">
            {previews.flatMap(p => p.result?.flags || [])
                     .filter((f, i, a) => a.indexOf(f) === i)
                     .map((f, i) => <div key={i}>⚠ {f}</div>)}
          </div>
        )}
        <p className="text-gray-600 text-xs mt-2">
          Which shipping methods are free over a threshold (and their standard rate) is set
          per method in <span className="text-gray-400">Shipping → methods</span>.
        </p>
      </div>
    </div>
  );
}
